import { createUseStyles } from "react-jss";
import { useCurrentUser, useSession } from "../../session/SessionContext";
import type { VetBoatsTheme } from "../../theme";
import { BrandMark } from "../brand/BrandMark";
import type { Route } from "../../routes";
import {
  DEFAULT_ROUTE,
  navItemsFor,
  navKeyFor,
  routeToHash,
} from "../../routes";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  bar: {
    background: theme.color.header,
    color: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 10,
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  },
  inner: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "0 16px",
    display: "flex",
    alignItems: "center",
    gap: 20,
    minHeight: 68,
    flexWrap: "wrap",
    [theme.narrow]: { gap: 8, padding: "8px 12px" },
  },
  brand: {
    textDecoration: "none",
    display: "inline-flex",
    paddingRight: 8,
    // On phones the logo gets its own row so the links can wrap across the full width.
    [theme.narrow]: { flexBasis: "100%" },
  },
  links: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
    flex: 1,
    [theme.narrow]: { flexBasis: "100%" },
  },
  link: {
    color: "#dbe4f0",
    textDecoration: "none",
    padding: "7px 11px",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 15,
    "&:hover": { background: "rgba(255,255,255,0.1)", color: "#fff" },
  },
  active: { background: "rgba(255,255,255,0.16)", color: "#fff" },
  user: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 14,
    whiteSpace: "nowrap",
  },
  signOut: {
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.35)",
    color: "#fff",
    borderRadius: 6,
    padding: "5px 10px",
    cursor: "pointer",
    font: "inherit",
    "&:hover": { background: "rgba(255,255,255,0.1)" },
  },
}));

export const NavBar = ({ route }: { route: Route }) => {
  const classes = useStyles();
  const user = useCurrentUser();
  const { signOut } = useSession();
  const activeKey = navKeyFor(route);

  return (
    <header className={classes.bar}>
      <nav className={classes.inner} aria-label="Main">
        <a
          href={routeToHash(DEFAULT_ROUTE)}
          className={classes.brand}
          aria-label="VetBoats home"
        >
          <BrandMark tone="onDark" />
        </a>
        <div className={classes.links}>
          {navItemsFor(user.role).map((item) => (
            <a
              key={item.key}
              href={routeToHash(item.route)}
              className={`${classes.link} ${item.key === activeKey ? classes.active : ""}`}
              aria-current={item.key === activeKey ? "page" : undefined}
            >
              {item.label}
            </a>
          ))}
        </div>
        <div className={classes.user}>
          <span>
            {user.displayName}
            {user.role === "admin" ? " · admin" : ""}
          </span>
          <button
            type="button"
            className={classes.signOut}
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </nav>
    </header>
  );
};
